/* ==========================================================================
   Grandseal motion layer
   --------------------------------------------------------------------------
   1. Reveals section content as it scrolls into view.
   2. Lets the hero copy + overlay fade out after their entrance, then brings
      them back on hover (mouse), tap (touch) or keyboard focus.
   3. Swaps the hero video for a portrait cut on portrait screens.

   Safety notes:
   - The reveal and hero-idle behaviour switch off under prefers-reduced-motion
     or without IntersectionObserver, in which case nothing is ever hidden.
   - The hidden state is added here (not in CSS), so a script failure leaves
     the page fully visible rather than blank.
   - A timed fallback reveals anything still hidden in the viewport.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_IO = 'IntersectionObserver' in window;

  var STAGGER_MS = 70;
  var MAX_STEPS = 6; // cap so long grids don't crawl in
  var HERO_HOLD_MS = 3800; // how long the hero copy stays before fading out
  var HERO_WAKE_MS = 6000; // how long a tap keeps it back on screen

  var observer = HAS_IO && new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('gs-in');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  function prep(el, index) {
    if (!el || el.nodeType !== 1 || el.dataset.gsReveal) return;
    el.dataset.gsReveal = '1';
    el.classList.add('gs-reveal');

    var step = Math.min(index || 0, MAX_STEPS);
    if (step > 0) el.style.setProperty('--gs-delay', step * STAGGER_MS + 'ms');

    observer.observe(el);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function collect(section) {
    var wrapper = section.querySelector('.section-content-wrapper');
    var items = section.querySelectorAll('.resource-list__item');

    // Sections built from a list (categories, products, blog posts):
    // reveal the heading first, then stagger the cards.
    if (items.length) {
      if (wrapper) {
        toArray(wrapper.children).forEach(function (child) {
          if (!child.querySelector('.resource-list__item')) prep(child, 0);
        });
      }
      toArray(items).forEach(function (item, i) {
        prep(item, i);
      });
      return;
    }

    if (!wrapper) return;
    var kids = toArray(wrapper.children);

    // A single wrapping group would reveal as one lump — step inside it so the
    // heading, copy and button arrive in sequence.
    if (kids.length === 1 && kids[0].classList.contains('group-block')) {
      var inner = kids[0].querySelector('.group-block-content');
      if (inner && inner.children.length > 1) {
        toArray(inner.children).forEach(function (el, i) {
          prep(el, i);
        });
        return;
      }
    }

    kids.forEach(function (el, i) {
      prep(el, i);
    });
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('.shopify-section:not(.header-section)');

    toArray(sections).forEach(function (section) {
      if (section.closest('footer')) return;
      collect(section);
    });
  }

  function safetyNet() {
    toArray(document.querySelectorAll('.gs-reveal:not(.gs-in)')).forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('gs-in');
      }
    });
  }

  /**
   * After the hero copy has introduced itself, hand the stage back to the
   * video. CSS takes over from there: hovering or tabbing into the hero fades
   * the copy and overlay back in.
   *
   * Touch screens have no hover, so a tap adds .gs-hero-awake for a slightly
   * longer window — there is no pointer-leave to dismiss it.
   *
   * Skipped in the theme editor, where a hero that keeps vanishing would make
   * editing the copy miserable.
   */
  function heroIdle() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    var section = document.querySelector('[id$="__hero_intro"], #shopify-section-hero_intro');
    if (!section) return;

    var target = section.querySelector('.section');
    if (!target || target.dataset.gsHero) return;
    target.dataset.gsHero = '1';

    window.setTimeout(function () {
      target.classList.add('gs-hero-dim');
    }, HERO_HOLD_MS);

    var wakeTimer;
    target.addEventListener('click', function () {
      if (!target.classList.contains('gs-hero-dim')) return;
      target.classList.add('gs-hero-awake');
      window.clearTimeout(wakeTimer);
      wakeTimer = window.setTimeout(function () {
        target.classList.remove('gs-hero-awake');
      }, HERO_WAKE_MS);
    });
  }

  /**
   * Swaps the hero video for a portrait cut on portrait screens.
   *
   * Horizon's video-background component copies each source's
   * `data-video-source` onto `src` and calls load(), so rewriting both
   * attributes works whether this runs before or after that component.
   *
   * Runs regardless of motion preference: this is about framing, not motion.
   */
  function portraitVideo() {
    var dataEl = document.getElementById('gs-portrait-hero-video');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.sources || !data.sources.length) return;

    var section = document.querySelector('[id$="__hero_intro"], #shopify-section-hero_intro');
    if (!section) return;

    var video = section.querySelector('video');
    if (!video) return;

    var nodes = toArray(video.querySelectorAll('source'));
    if (!nodes.length) return;

    // Remember what the theme rendered so we can switch back.
    var landscape = nodes.map(function (node) {
      return {
        url: node.getAttribute('data-video-source') || node.getAttribute('src') || '',
        type: node.getAttribute('type') || 'video/mp4'
      };
    });

    var showingPortrait = null;

    function apply(portrait) {
      if (showingPortrait === portrait) return;
      showingPortrait = portrait;

      var list = portrait ? data.sources : landscape;

      nodes.forEach(function (node, i) {
        var source = list[i] || list[list.length - 1];
        if (!source || !source.url) return;
        node.setAttribute('data-video-source', source.url);
        node.setAttribute('src', source.url);
        if (source.type) node.setAttribute('type', source.type);
      });

      try {
        video.load();
      } catch (e) {
        /* the component will load it */
      }
    }

    var mq = window.matchMedia('(orientation: portrait)');
    apply(mq.matches);

    var onChange = function () {
      apply(mq.matches);
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  function boot() {
    portraitVideo();
    if (REDUCED || !HAS_IO) return;
    run();
    heroIdle();
    window.setTimeout(safetyNet, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections on edit; re-scan the new markup.
  document.addEventListener('shopify:section:load', function (event) {
    portraitVideo();
    if (REDUCED || !HAS_IO) return;
    run(event.target);
    window.setTimeout(safetyNet, 400);
  });
})();
